import * as XLSX from 'xlsx';

/** Cantidad de decimales para costos y precios en importación / previsualización. */
export const PRECIOS_IMPORT_DECIMALES = 2;

export function roundPrecioImport(value: number): number {
  const factor = 10 ** PRECIOS_IMPORT_DECIMALES;
  return Math.round(value * factor) / factor;
}

export function formatPrecioImport(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return roundPrecioImport(value).toLocaleString('es-AR', {
    minimumFractionDigits: PRECIOS_IMPORT_DECIMALES,
    maximumFractionDigits: PRECIOS_IMPORT_DECIMALES,
  });
}

export function formatPrecioImportInput(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return roundPrecioImport(value).toLocaleString('es-AR', {
    minimumFractionDigits: PRECIOS_IMPORT_DECIMALES,
    maximumFractionDigits: PRECIOS_IMPORT_DECIMALES,
  });
}

export function parsePrecioImportInput(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  const n = parseFloat(normalized);
  if (!Number.isFinite(n) || n < 0) return null;
  return roundPrecioImport(n);
}

/** Convierte letra de columna Excel (A, B, …, AA) a índice base 0. */
export function excelColumnToIndex(col: string): number {
  const s = col.trim().toUpperCase();
  if (!s || !/^[A-Z]+$/.test(s)) return -1;
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    n = n * 26 + (s.charCodeAt(i) - 64);
  }
  return n - 1;
}

export function getExcelCellValue(sheet: XLSX.WorkSheet, filaExcel: number, colLetter: string): unknown {
  const col = excelColumnToIndex(colLetter);
  if (col < 0 || filaExcel < 1) return undefined;
  const addr = XLSX.utils.encode_cell({ r: filaExcel - 1, c: col });
  return sheet[addr]?.v;
}

/** Escribe un valor numérico en una celda del libro (numeración de fila igual que Excel). */
export function setExcelCellNumber(
  sheet: XLSX.WorkSheet,
  filaExcel: number,
  colLetter: string,
  value: number
): void {
  const col = excelColumnToIndex(colLetter);
  if (col < 0 || filaExcel < 1 || !Number.isFinite(value)) return;
  const addr = XLSX.utils.encode_cell({ r: filaExcel - 1, c: col });
  sheet[addr] = { t: 'n', v: roundPrecioImport(value) };
}

export interface ApplyPreviewEditsToWorkbookParams {
  nombreHoja: string;
  columnaCosto?: string;
  columnaPrecioNeto?: string;
}

export interface PreviewFilaExcelEditable {
  filaExcel: number;
  estado: string;
  costoNuevo?: number | null;
  precioNetoNuevo?: number | null;
}

/** Clona el workbook y aplica los valores editados en la previsualización. */
export function clonarWorkbook(workbook: XLSX.WorkBook): XLSX.WorkBook {
  const buf = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return XLSX.read(buf, { type: 'array' });
}

export function applyPreviewEditsToWorkbook(
  workbook: XLSX.WorkBook,
  params: ApplyPreviewEditsToWorkbookParams,
  filas: PreviewFilaExcelEditable[]
): void {
  const sheet = workbook.Sheets[params.nombreHoja];
  if (!sheet) return;

  for (const fila of filas) {
    if (fila.estado !== 'actualizar') continue;
    if (params.columnaCosto && fila.costoNuevo != null && Number.isFinite(fila.costoNuevo)) {
      setExcelCellNumber(sheet, fila.filaExcel, params.columnaCosto, fila.costoNuevo);
    }
    if (
      params.columnaPrecioNeto &&
      fila.precioNetoNuevo != null &&
      Number.isFinite(fila.precioNetoNuevo)
    ) {
      setExcelCellNumber(sheet, fila.filaExcel, params.columnaPrecioNeto, fila.precioNetoNuevo);
    }
  }
}

export function workbookToXlsxFile(workbook: XLSX.WorkBook, fileName: string): File {
  const buf = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const nombre = fileName.toLowerCase().endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  return new File([buf], nombre, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function parseExcelNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return roundPrecioImport(value);
  const s = String(value).trim();
  if (!s) return null;
  // Formato argentino: 1.234,56
  if (s.includes(',')) {
    const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? roundPrecioImport(n) : null;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? roundPrecioImport(n) : null;
}

export function parseExcelCodigo(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : String(value);
  }
  return String(value).trim();
}

export interface ExcelFilaPreciosImport {
  filaExcel: number;
  codigo: string;
  costoExcel?: number | null;
  precioNetoExcel?: number | null;
}

export interface ParseExcelPreciosImportParams {
  workbook: XLSX.WorkBook;
  nombreHoja: string;
  primeraFilaDatos: number;
  ultimaFilaDatos?: number;
  /** Si el rango del Excel es corto, usar filas leídas por el servidor como guía. */
  filasLeidasApi?: number;
  columnaCodigo: string;
  columnaCosto?: string;
  columnaPrecioNeto?: string;
}

export function parseExcelPreciosImportRows(params: ParseExcelPreciosImportParams): ExcelFilaPreciosImport[] {
  const sheet = params.workbook.Sheets[params.nombreHoja];
  if (!sheet) return [];

  const range = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']) : null;
  const ultimaPorApi =
    params.filasLeidasApi && params.filasLeidasApi > 0
      ? params.primeraFilaDatos + params.filasLeidasApi - 1
      : undefined;
  const ultimaPorHoja = range ? range.e.r + 1 : params.primeraFilaDatos;
  const ultimaExplicita =
    params.ultimaFilaDatos !== undefined && Number.isFinite(params.ultimaFilaDatos)
      ? params.ultimaFilaDatos
      : undefined;
  const ultima = Math.max(
    ultimaExplicita ?? 0,
    ultimaPorApi ?? 0,
    ultimaPorHoja,
    params.primeraFilaDatos
  );

  const filas: ExcelFilaPreciosImport[] = [];
  for (let fila = params.primeraFilaDatos; fila <= ultima; fila++) {
    const codigo = parseExcelCodigo(getExcelCellValue(sheet, fila, params.columnaCodigo));
    const costoExcel = params.columnaCosto
      ? parseExcelNumber(getExcelCellValue(sheet, fila, params.columnaCosto))
      : null;
    const precioNetoExcel = params.columnaPrecioNeto
      ? parseExcelNumber(getExcelCellValue(sheet, fila, params.columnaPrecioNeto))
      : null;

    if (!codigo && costoExcel === null && precioNetoExcel === null) continue;

    filas.push({
      filaExcel: fila,
      codigo,
      costoExcel,
      precioNetoExcel,
    });
  }
  return filas;
}

/** Extrae el número de fila de mensajes tipo "Fila 12: …". */
export function parseFilaDesdeErrorFila(mensaje: string): number | null {
  const m = /fila\s+(\d+)/i.exec(mensaje);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

